import { Fragment, useEffect } from 'react';
import type { FC } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import { PageType } from '@vgtv/api-client/lib/page';
import type { CnpStory } from '@vgtv/api-client/lib/cnp_story';

import { PageComponents } from '../components/PageComponents/PageComponents';
import { PageMetadata } from '../components/PageMetadata/PageMetadata';
import { pageStartsWithSplash } from '../shared/helpers/pageHelpers';
import { PageHeaderSplash } from '../components/PageHeaderSplash/PageHeaderSplash';
import { getPath, routeNames } from '../shared/helpers/routes';
import { PageSpinner } from '../components/PageSpinner/PageSpinner';
import { usePageViewTracking } from '../components/utils/usePageViewTracking';
import { replaceUrl } from '../helpers/replaceUrl';
import { config } from '../config';

import { usePageConfig } from './usePageConfig';
import { NotFoundPage } from './NotFoundPage';

const StoryPage: FC = () => {
  const { storyId } = useParams<{ storyId: CnpStory['id'] }>();

  const { isFetching, pageConfig, isFetched } = usePageConfig({
    identifier: storyId,
    pageType: PageType.STORY,
    vendor: config.provider,
  });

  const history = useHistory();

  useEffect(() => {
    if (pageConfig && pageConfig.story) {
      const { id: storyId, title } = pageConfig.story;

      replaceUrl(history, getPath(routeNames.story, { storyId, title }));
    }
  }, [pageConfig, history]);

  usePageViewTracking({
    pageType: PageType.STORY,
    entity: pageConfig ? pageConfig.story : undefined,
  });

  if (!pageConfig) {
    if (isFetching) {
      return <PageSpinner />;
    }

    if (!isFetching && isFetched) {
      return <NotFoundPage />;
    }

    return null;
  }

  const { components } = pageConfig;

  const startsWithSplash = pageStartsWithSplash(pageConfig);

  return (
    <Fragment>
      <PageMetadata
        entity={pageConfig.story}
        pageType={PageType.STORY}
        overrides={pageConfig.meta}
      />
      {!startsWithSplash && <PageHeaderSplash pageConfiguration={pageConfig} />}
      <PageComponents componentsConfig={components} />
    </Fragment>
  );
};

export { StoryPage };
